import React, { useContext, useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import ButtonBase from '@mui/material/ButtonBase';

import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { DeleteConfirmationModalContext } from '../contexts/DeleteConfirmationModalContext';

import ImagePreviewModal from './ImagePreviewModal';
import { Media, isVideo } from './media';

interface ImagePreviewThumbnailProps {
  image: Media;
  altText: string;
}

const ImagePreviewThumbnail: React.FC<ImagePreviewThumbnailProps> = ({ image, altText }) => {
  const { setModelLabel, setDeleteConfirmationModalOpen, setRecordToDelete, setDeleteURL } =
    useContext(DeleteConfirmationModalContext);

  const [isHovered, setIsHovered] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  if (!image) return null;

  const isVideoMedia = isVideo(image);

  const handleDeleteClicked = () => {
    setDeleteURL('backflows/backflow_log_images/');
    setModelLabel(isVideoMedia ? 'Video' : 'Image');
    setDeleteConfirmationModalOpen(true);
    setRecordToDelete(image);
    setModalOpen(false); // Close the preview modal when opening delete confirmation
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <>
      <Card
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          position: 'relative',
          height: '100%',
          transition: 'all 0.3s ease',
          boxShadow: isHovered ? 4 : 1,
          transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
          cursor: 'pointer',
          '&:hover': {
            boxShadow: 4,
          },
        }}
      >
        <ButtonBase
          onClick={handleOpenModal}
          aria-label={`View ${altText || (isVideoMedia ? 'Video' : 'Image')}`}
          sx={{
            display: 'block',
            width: '100%',
            height: 200,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {isVideoMedia ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                bgcolor: 'grey.900',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                transition: 'background-color 0.3s',
                '&:hover': {
                  bgcolor: 'grey.800',
                },
              }}
            >
              <PlayArrowIcon
                sx={{
                  fontSize: 72,
                  color: 'white',
                  opacity: 0.9,
                  transition: 'transform 0.3s',
                  transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                }}
              />
            </Box>
          ) : (
            <CardMedia
              component="img"
              image={image.url}
              alt={altText}
              sx={{
                height: 200,
                objectFit: 'cover',
                width: '100%',
              }}
            />
          )}
        </ButtonBase>
      </Card>

      <ImagePreviewModal
        open={modalOpen}
        onClose={handleCloseModal}
        image={image}
        altText={altText}
        isVideo={isVideoMedia}
        onDelete={handleDeleteClicked}
      />
    </>
  );
};

export default ImagePreviewThumbnail;
